import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';
import FairTopListFilter from '../components/FairTopListFilter';
import FairTopListForm from '../components/FairTopListForm';
import fairTopSettingService from '../fairTopSettingService';
import { FairTopSetting, SearchFairTopListFilter } from '../models';

export type FairTopListFormValues = {
  fairtops: FairTopSetting[];
};

function FairTopListPage() {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // refs
  const btnSaveRef = useRef<HTMLButtonElement>(null);

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // states
  const [filter, setFilter] = useState<SearchFairTopListFilter>({
    targetDate: new Date(),
  });

  // setup react hook form
  const methods = useForm<FairTopListFormValues>({
    defaultValues: {
      fairtops: [],
    },
    mode: 'onBlur',
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = methods;

  // events
  function loadFairTopData() {
    (async () => {
      try {
        const response = await fairTopSettingService.getAll(filter.targetDate);
        reset({ fairtops: response.data });
        // reset({ fairtops: fairTopListMockData });
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function handleFilter(data: SearchFairTopListFilter) {
    setFilter(data);
  }

  async function onSubmit(formValues: FairTopListFormValues) {
    try {
      const response = await fairTopSettingService.update(formValues.fairtops);
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
      loadFairTopData();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  // side effects
  useEffect(() => {
    loadFairTopData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <div className="mb-3">
            <FeatureTitleBar title={t('FairTopListPage.head_title')}></FeatureTitleBar>
          </div>
          <FormProvider {...methods}>
            <div className="mb-2">
              <FairTopListFilter onFilter={handleFilter} />
            </div>

            {/* actions */}
            <div className="text-end mb-2">
              <Button
                ref={btnSaveRef}
                className="btn--dark-green ms-3"
                disabled={!isDirty}
                onClick={handleSubmit(onSubmit)}
              >
                {t('FairTopListPage.save')}
              </Button>
            </div>

            <FairTopListForm />
          </FormProvider>
        </div>
      </div>
    </div>
  );
}

export default FairTopListPage;
